import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui';
import { authFetch } from '../../lib/api/authFetch';
import { API_BASE } from '../../lib/api/version';
import { patchOnboarding } from '../../lib/api/accounts';

const EPIC_TEMPLATE = `## Goal

Describe what this epic should achieve.

## Acceptance criteria

- [ ] Feature works end-to-end
- [ ] Tests pass

## Out of scope

- N/A`;

interface Props {
  projectId: string | null;
  repoFullName: string | null;
  accountId: string | null;
  onSkip: () => void;
  onBack: () => void;
}

export const StepFirstEpic: React.FC<Props> = ({ projectId, repoFullName, accountId, onSkip, onBack }) => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState(EPIC_TEMPLATE);
  const [creating, setCreating] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleCreate = async () => {
    if (!title.trim() || !repoFullName) return;
    setCreating(true);
    setErrorMsg('');
    try {
      const r = await authFetch(`${API_BASE}/epics`, {
        method: 'POST',
        body: JSON.stringify({
          kind: 'epic',
          repoFullName,
          title: title.trim(),
          body,
          projectId,
          accountId,
        }),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: { message?: string } };
        const msg = j?.error?.message ?? 'Failed to create epic';
        if (msg.toLowerCase().includes('github app') || msg.toLowerCase().includes('not installed')) {
          setErrorMsg(msg + ' — go back to Step 6 to install the App first.');
        } else {
          setErrorMsg(msg);
        }
        return;
      }
      const data = (await r.json()) as { data?: { id?: string | number } };
      const epicId = data.data?.id;
      // Mark onboarding complete
      if (accountId) {
        try {
          await patchOnboarding(accountId, { step: 'completed', completedAt: new Date().toISOString() });
        } catch { /* best-effort */ }
      }
      navigate(epicId ? `/epics/${epicId}` : '/', { replace: true });
    } catch (err) {
      setErrorMsg((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Create your first epic and watch Forge plan, build, test, and deploy it — live.
      </p>

      <div className="space-y-1">
        <label className="block text-xs font-medium text-slate-700">Epic title <span className="text-danger-600">*</span></label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Add user authentication with email + password"
          className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          autoFocus
        />
      </div>

      <div className="space-y-1">
        <label className="block text-xs font-medium text-slate-700">Description (Markdown)</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={10}
          className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono"
        />
      </div>

      {!repoFullName && (
        <p className="text-xs text-warning-700 bg-warning-50 border border-warning-200 rounded px-3 py-2">
          No repo connected — go back to Step 6 to connect a repo, or skip this step.
        </p>
      )}
      {errorMsg && <p className="text-xs text-danger-700">{errorMsg}</p>}

      <div className="flex gap-3 pt-2">
        <Button variant="ghost" size="md" onClick={onBack} className="flex-1">Back</Button>
        <Button variant="ghost" size="md" onClick={onSkip} className="flex-1">Skip</Button>
        <Button
          variant="primary"
          size="md"
          onClick={handleCreate}
          loading={creating}
          disabled={!title.trim() || !repoFullName}
          className="flex-1"
        >
          {creating ? 'Creating…' : 'Create epic →'}
        </Button>
      </div>
    </div>
  );
};
